import axios from "axios";
import { getClientDataFromAdyogiAPI } from "../clientData/utils";

const LOGIN_API_BASE = process.env.REACT_APP_LOGIN_API ?? "https://app1.adyogi.com";
const AUTHORIZATION_HEADER = process.env.REACT_APP_AUTHORIZATION_FOR_NORMAL_USER;

const readJson = (raw, fallback) => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const normalizeTokenList = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") return normalizeTokenList(readJson(raw, null));
  if (Array.isArray(raw?.account_refresh_tokens)) return raw.account_refresh_tokens;
  return [];
};

export const accountRefreshTokensFromClientRecord = (record) =>
  normalizeTokenList(record?.amazon_setup?.account_refresh_tokens);

export const fetchSetupAccountRefreshTokens = async (clientId) => {
  if (!clientId) return [];
  const response = await getClientDataFromAdyogiAPI(clientId);
  const record = response?.data?.results?.[0] || {};
  return accountRefreshTokensFromClientRecord(record);
};

export const accountsForType = (tokenType, setupTokens = []) =>
  normalizeTokenList(setupTokens).filter(
    (account) =>
      account?.token_type === tokenType &&
      String(account?.status || "").toLowerCase() !== "deleted"
  );

export const hasActiveAccount = (tokenType, setupTokens = []) =>
  accountsForType(tokenType, setupTokens).some(
    (account) => String(account?.status || "").toLowerCase() === "active"
  );

export const SINGLE_ACCOUNT_MESSAGE =
  "Currently we support only one connected account.";

export const activeAccountConflict = (setupTokens, { tokenType, tokenId } = {}) => {
  const activeAccount = normalizeTokenList(setupTokens).find(
    (account) =>
      String(account?.status || "").toLowerCase() === "active" &&
      account?.token_id !== tokenId
  );
  if (!activeAccount) return "";
  if (
    tokenType &&
    activeAccount.token_type &&
    activeAccount.token_type !== tokenType
  ) {
    const label = activeAccount.token_type === "vendor" ? "Vendor" : "Seller";
    return `A ${label} account is already connected. ${SINGLE_ACCOUNT_MESSAGE}`;
  }
  return SINGLE_ACCOUNT_MESSAGE;
};

const shortUniqueId = () => {
  const source =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "")
      : `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
  return source.slice(0, 8);
};

export const createAccountTokenId = (clientId, tokenType, existingIds = []) => {
  const used = new Set(existingIds);
  let tokenId = `${clientId}_${tokenType}_${shortUniqueId()}`;
  while (used.has(tokenId)) {
    tokenId = `${clientId}_${tokenType}_${shortUniqueId()}`;
  }
  return tokenId;
};

const apiErrorMessage = (error) =>
  error?.response?.data?.error ||
  error?.message ||
  "Unable to save the Amazon account";

export const updateAccountRefreshToken = async (clientId, data) => {
  try {
    const response = await axios.post(
      `${LOGIN_API_BASE}/api/amzn/updateAccountRefreshToken`,
      { clientId, data },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: AUTHORIZATION_HEADER,
        },
      }
    );
    const body = response?.data || {};
    if (body.success === false) {
      throw new Error(body.error || "Unable to save the Amazon account");
    }
    return body;
  } catch (error) {
    if (error?.response) {
      throw new Error(apiErrorMessage(error));
    }
    throw error;
  }
};

export const saveConnectedAccount = async ({
  clientId,
  tokenType,
  refreshToken,
  sellingPartnerId,
  accountName,
  tokenId,
  setupTokens = [],
}) => {
  const partnerId = sellingPartnerId || "";
  const matchedPartner = !tokenId && partnerId
    ? normalizeTokenList(setupTokens).find(
        (account) =>
          account?.token_type === tokenType &&
          account?.selling_partner_id === partnerId
      )
    : null;
  const resolvedTokenId =
    tokenId ||
    matchedPartner?.token_id ||
    createAccountTokenId(
      clientId,
      tokenType,
      normalizeTokenList(setupTokens).map((account) => account?.token_id)
    );
  const conflict = activeAccountConflict(setupTokens, {
    tokenType,
    tokenId: resolvedTokenId,
  });
  if (conflict) throw new Error(conflict);

  const account = {
    token_id: resolvedTokenId,
    refresh_token: refreshToken || "",
    token_type: tokenType,
    status: "active",
    account_name: accountName || partnerId,
    selling_partner_id: partnerId,
  };
  const result = await updateAccountRefreshToken(clientId, account);
  return {
    ...account,
    action: result?.action || "",
    reconnected: Boolean(tokenId) && resolvedTokenId === tokenId,
  };
};

export const updateAccountName = async (clientId, tokenId, accountName) =>
  updateAccountRefreshToken(clientId, {
    token_id: tokenId,
    account_name: accountName,
  });

export const disconnectAccount = async (clientId, tokenId) =>
  updateAccountRefreshToken(clientId, {
    token_id: tokenId,
    status: "disabled",
  });

export const deleteAccount = async (clientId, tokenId) =>
  updateAccountRefreshToken(clientId, {
    token_id: tokenId,
    status: "deleted",
  });
